/**
 * Port of cdc-web `src/utils/timezoneAbbreviation.ts`（1:1）。
 *
 * ## 为什么有这张表
 *
 * `Intl.DateTimeFormat({ timeZoneName: 'short' })`（dayjs 的 `z`/`zz` token
 * 底层用的就是这个）只为少数时区提供 *命名* 缩写（EDT/EST/PST/PDT/…），
 * 多数是北美时区。世界上大部分地区（香港、东京、新加坡、雅加达、上海……）的
 * CLDR "short" 形式就是偏移量，因此即使带完整 ICU 数据，Intl 也返回
 * "GMT+8"。这是 CLDR 数据事实，不是运行时 bug。
 *
 * 为了让这些时区渲染出可识别的缩写（"HKT"），我们维护自己的小表，键为浏览器
 * `Intl.DateTimeFormat().resolvedOptions().timeZone` 解析出的 IANA 时区。美国
 * 时区刻意不入表：CLDR 已有命名（EDT/EST/…），覆盖只会增加风险。表外的任何
 * 时区回退到 Intl "GMT±x" 标签——优雅降级，不会崩溃。
 *
 * 表中条目与 og-app 的等价表（`src/utils/timezone-abbreviation-map.ts`，
 * OG-9448）保持同步，两个平台对同一时区显示相同缩写。
 *
 * ## 歧义须知
 *
 * 时区缩写天然有歧义（CST = 中国标准时间 也是 美国中部时间；BST = 英国夏令
 * 时间 也是 百慕大；IST = 印度 也是 爱尔兰）。这里的条目取其 IANA 时区对应的
 * 含义，上下文无歧义。保持表小而精，只加产品真正覆盖的时区——每条都是维护承诺。
 */

/**
 * 固定缩写（无 DST 的时区——缩写永不改变）或该时区 UTC 偏移分钟 → 缩写的映射
 * （有 DST 的时区——缩写随季节翻转，如 Europe/London：冬 GMT、夏 BST）。
 */
type ZoneAbbreviationEntry = string | Readonly<Record<number, string>>

/**
 * 覆盖表。键是规范 IANA 时区标识符（大小写敏感，正如 `resolvedOptions().timeZone`
 * 返回的）。
 *
 * 偏移键是事件瞬间**东经 UTC 分钟数**（UTC+8 → 480，UTC-5 → -300）。对 DST
 * 时区按事件瞬间实际落到的偏移查缩写，所以 7 月的事件解析为 "BST"/"EDT"，
 * 1 月的事件解析为 "GMT"/"EST"。
 */
const TIMEZONE_ABBREVIATIONS: Readonly<Record<string, ZoneAbbreviationEntry>> = {
  // --- Asia (no DST — fixed abbreviations) ---
  'Asia/Hong_Kong': 'HKT',
  'Asia/Tokyo': 'JST',
  'Asia/Shanghai': 'CST',
  'Asia/Taipei': 'CST',
  'Asia/Singapore': 'SGT',
  'Asia/Kuala_Lumpur': 'MYT',
  'Asia/Jakarta': 'WIB',
  'Asia/Makassar': 'WITA',
  'Asia/Jayapura': 'WIT',
  'Asia/Seoul': 'KST',
  'Asia/Kolkata': 'IST',
  'Asia/Bangkok': 'ICT',
  'Asia/Manila': 'PHT',
  'Asia/Karachi': 'PKT',
  'Asia/Dhaka': 'BST',
  'Asia/Dubai': 'GST',
  'Asia/Riyadh': 'AST',

  // --- Pacific / Oceania & Australia (DST) ---
  'Australia/Sydney': { 600: 'AEST', 660: 'AEDT' },
  'Australia/Melbourne': { 600: 'AEST', 660: 'AEDT' },
  'Australia/Brisbane': 'AEST',
  'Australia/Perth': 'AWST',
  'Pacific/Auckland': { 720: 'NZST', 780: 'NZDT' },

  // --- Europe (DST) ---
  'Europe/London': { 0: 'GMT', 60: 'BST' },
  'Europe/Paris': { 60: 'CET', 120: 'CEST' },
  'Europe/Berlin': { 60: 'CET', 120: 'CEST' },
  'Europe/Madrid': { 60: 'CET', 120: 'CEST' },
  'Europe/Rome': { 60: 'CET', 120: 'CEST' },
  'Europe/Amsterdam': { 60: 'CET', 120: 'CEST' },
  'Europe/Moscow': 'MSK',

  // --- Africa (no DST — fixed abbreviations) ---
  'Africa/Johannesburg': 'SAST',
  'Africa/Lagos': 'WAT',
  'Africa/Cairo': 'EET',

  // --- South America (Brazil abolished DST in 2019, but keep both) ---
  // Negative numeric keys are quoted: a bare `-180` is an expression, not a
  // valid object-literal key. Number-index lookup coerces, so `-180` still
  // resolves to the `'-180'` entry.
  'America/Sao_Paulo': { '-180': 'BRT', '-120': 'BRST' },
}

/**
 * 为给定 IANA 时区解析精选缩写，当该时区有 DST 时用 `offsetMinutes` 选 DST
 * 正确的变体。
 *
 * @returns 缩写，当时区未入表（调用方应回退 Intl short 标签）或 DST 条目存在
 *   但偏移无法解析时为 `null`。
 */
export const resolveTimezoneAbbreviation = (
  timeZone: string,
  offsetMinutes: number | null,
): string | null => {
  const entry = TIMEZONE_ABBREVIATIONS[timeZone]
  if (entry === undefined) return null
  if (typeof entry === 'string') return entry
  if (offsetMinutes === null) return null
  return entry[offsetMinutes] ?? null
}

/**
 * 该时区在给定瞬间的**东经 UTC 偏移分钟数**（UTC+8 → 480，UTC-5 → -300）。
 * 用「目标时区墙钟时间 vs UTC」的差值 trick，只依赖 `Intl.DateTimeFormat`
 * 的字段 parts（无需 `timeZoneName: 'longOffset'` 支持）。
 *
 * @returns 偏移无法确定时（无效日期、不支持的时区、parts 不可解析）为 `null`。
 */
export const getZoneOffsetMinutes = (date: Date, timeZone: string): number | null => {
  const ts = date.getTime()
  if (Number.isNaN(ts)) return null
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(date)
    const get = (type: string): string => parts.find(p => p.type === type)?.value ?? ''
    const year = parseInt(get('year'), 10)
    const month = parseInt(get('month'), 10)
    const day = parseInt(get('day'), 10)
    let hour = parseInt(get('hour'), 10)
    const minute = parseInt(get('minute'), 10)
    const second = parseInt(get('second'), 10)
    if ([year, month, day, hour, minute, second].some(Number.isNaN)) return null
    // `hour12: false` 在部分引擎午夜可能输出 "24"，归一化。
    if (hour === 24) hour = 0
    const wallClockAsUtc = Date.UTC(year, month - 1, day, hour, minute, second)
    return Math.round((wallClockAsUtc - ts) / 60000)
  } catch {
    return null
  }
}

/**
 * 将 `date` 在 `timeZone` 的墙钟时间格式化为 Intl "short" 时区标签，作为无
 * 精选覆盖时的回退。Intl 无法解析标签（不支持的时区、无效日期）时返回 `""`。
 */
const formatIntlShortTimezone = (date: Date, timeZone: string): string => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'short',
    }).formatToParts(date)
    return parts.find(p => p.type === 'timeZoneName')?.value ?? ''
  } catch {
    return ''
  }
}

/**
 * 返回 `date` 在 `timeZone` 的短时区缩写标签（如 "PST"、"HKT"、"GMT+7"），
 * 优先精选覆盖表，其次 Intl "short" 形式。
 *
 * `timeZone` 默认为浏览器本地时区
 * （`Intl.DateTimeFormat().resolvedOptions().timeZone`）——显示特定时区时显式
 * 传入（如 `dayjs.tz.guess()`、`'America/New_York'`）。
 *
 * 针对被显示的**实际瞬间**（非 `Date.now()`）计算，使标签匹配该瞬间的 DST
 * 状态——12 月"现在"渲染 7 月的事件必须显示 "BST" 而非 "GMT"。
 *
 * 表与 Intl 都无法解析时返回 `""`。调用方按需追加，缺失时退化纯日期/时间串，
 * 不会出现多余空格。
 */
export const getTimezoneLabel = (date: Date, timeZone?: string): string => {
  if (Number.isNaN(date.getTime())) return ''

  const zone = timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone
  const intlFallback = formatIntlShortTimezone(date, zone)

  const offsetMinutes = getZoneOffsetMinutes(date, zone)
  return resolveTimezoneAbbreviation(zone, offsetMinutes) ?? intlFallback
}

/**
 * 把 `getTimezoneLabel` 的结果追加到 `formatted`（空格分隔），时区无法解析时
 * 原样返回 `formatted`——避免时区未入表且 Intl 也无解时留下多余尾空格。
 */
export const withTimezoneLabel = (formatted: string, date: Date, timeZone?: string): string => {
  const tzLabel = getTimezoneLabel(date, timeZone)
  return tzLabel ? `${formatted} ${tzLabel}` : formatted
}
